// The check's working: inputs, computation, result.
//
// Moved here from FlagCard unchanged. This is the analytical artifact the whole
// product rests on — a reviewer has to be able to redo it on paper from what is
// on screen — so it is reused rather than reimplemented, and nothing about its
// substance is edited here. What this file adds to what FlagCard rendered is
// the row limit below, and the shared ScrollFrame around the two blocks that
// can be wider than the pane — the inputs and the working — so neither ever
// pushes the page sideways.

import { Label, ScrollFrame } from '../primitives';
import { Glossed } from '../Glossary';
import type { Receipt as ReceiptData } from '../../types';
import './Receipt.css';

/**
 * Above this many inputs the table scrolls inside its own container. Twelve
 * rows is about a screen: enough that the common receipt never scrolls, few
 * enough that a checklist receipt does not bury the result below the fold.
 */
export const INPUT_ROW_LIMIT = 12;

export default function Receipt({ receipt }: { receipt: ReceiptData }) {
  const tall = receipt.inputs.length > INPUT_ROW_LIMIT;

  return (
    <section className="fc__receipt" aria-label="The check">
      <header className="fc__receipt-head">
        <Label mono>The check · {receipt.kind}</Label>
        <span className="fc__receipt-note">
          Redo this yourself from the source document.
        </span>
      </header>

      <ScrollFrame
        className="fc__receipt-scroll-shell"
        frameClassName={`fc__receipt-scroll${tall ? ' fc__receipt-scroll--tall' : ''}`}
        label="Inputs to the check"
        hint="Wider than the pane. Scroll the inputs sideways, or focus them and use the arrow keys, to see the rest."
      >
        <table className="fc__receipt-table">
          <caption className="fc__sr">Inputs to the check</caption>
          <tbody>
            {receipt.inputs.map((input, i) => (
              <tr key={i}>
                <th scope="row">
                  <Glossed>{input.label}</Glossed>
                </th>
                <td>{input.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollFrame>

      {receipt.computation ? (
        <div className="fc__receipt-block">
          <Label>Computation</Label>
          {/* The working is set in mono with its whitespace preserved, so a
              long line is scrolled rather than rewrapped into a different
              sum. Same frame as every other wide block. */}
          <ScrollFrame
            label="The computation"
            hint="Wider than the pane. Scroll it sideways, or focus it and use the arrow keys, to see the rest."
          >
            <pre className="fc__computation">{receipt.computation}</pre>
          </ScrollFrame>
        </div>
      ) : null}

      <div className="fc__receipt-block">
        <Label>Result</Label>
        <p className="fc__receipt-result">
          <Glossed>{receipt.result}</Glossed>
        </p>
      </div>
    </section>
  );
}
